import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  RichTextContent,
  EditableSlotGroup,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { CTA_OUTLINE, CTA_SOLID, CTA_SURFACE_HOVER, CtaStyles, ctaOverLight, ctaOverPhoto } from './_cta';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ─── Slot group definitions ───────────────────────────────────────────────────

const apartmentCtaDef: SlotGroupDefinition = {
  label: 'Interior CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'See Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const communityCtaDef: SlotGroupDefinition = {
  label: 'Community CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const bandCtaDef: SlotGroupDefinition = {
  label: 'Feature Band CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore The Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const bandImageDef: SlotGroupDefinition = {
  label: 'Feature Band Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'property_featured_image_media.alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="amenities-slider-frame">
        <div className="amenities-slider-clip">
          {photos.map((photo, i) => (
            <VibesitesImage
              key={photo.image || i}
              url={photo.image!}
              alt={photo.alt_text || photo.title || heading}
              className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
              sizes="(min-width: 768px) 40vw, 100vw"
              priority
            />
          ))}
        </div>
      </div>

      {count > 1 && (
        <div className="amenities-slider-nav">
          <button
            type="button"
            className="amenities-slider-nav-btn"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          </button>
          <button
            type="button"
            className="amenities-slider-nav-btn"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Feature row — image slider beside a single-column amenity list + CTA
// ============================================================================

interface AmenityRowProps {
  headingNode: ReactNode;
  ctaNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
  imagePosition: 'left' | 'right';
}

function AmenityRow({ headingNode, ctaNode, headingText, items, photos, imagePosition }: AmenityRowProps) {
  const rowRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const contentPanel = (
    <div className="amenities-row-content">
      {headingNode}
      <ul className="amenities-feature-list">
        {items.map((item, i) => (
          <li key={i} className="amenities-feature-item text-[var(--vibe-color-text)]/75">
            <span className="amenities-feature-bullet" aria-hidden="true" />
            <span>{item.title || ''}</span>
          </li>
        ))}
      </ul>
      {ctaNode}
    </div>
  );

  const imagePanel = hasImage ? (
    <div className="amenities-row-image">
      <PhotoSlider photos={photos} heading={headingText} />
    </div>
  ) : null;

  return (
    <div
      ref={rowRef}
      className={`reveal-fade-up amenities-row amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
    >
      {imagePosition === 'left' ? (
        <>
          {imagePanel}
          {contentPanel}
        </>
      ) : (
        <>
          {contentPanel}
          {imagePanel}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

// Strip HTML tags from a richtext value so it can be used as a plain
// string (e.g. as fallback alt text on a photo slider).
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function AmenitiesList() {
  const communityHeadingRaw = useContentToken('amenitieslist.communityHeading');
  const apartmentHeadingRaw = useContentToken('amenitieslist.apartmentHeading');
  const communityHeadingText = stripHtml(communityHeadingRaw) || 'Community Amenities';
  const apartmentHeadingText = stripHtml(apartmentHeadingRaw) || 'Interior Luxuries';

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  const apartmentCta = useSlotGroup('amenitieslist.apartmentCta', apartmentCtaDef);
  const communityCta = useSlotGroup('amenitieslist.communityCta', communityCtaDef);
  const bandCta = useSlotGroup('amenitieslist.bandCta', bandCtaDef);
  const bandImage = useSlotGroup('amenitieslist.bandImage', bandImageDef);

  useRevealStyles();
  const bandRef = useScrollReveal<HTMLDivElement>();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="py-8 md:py-14 bg-[var(--vibe-color-background)]">
      <CtaStyles />
      {hasContent ? (
        <>
          {apartmentItems.length > 0 && (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <AmenityRow
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    label="Interior Heading"
                    defaultValue="<p>Everything you need is right here.</p>"
                    as="h2"
                    className="amenities-col-heading"
                  />
                }
                ctaNode={
                  <EditableSlotGroup groupPath="amenitieslist.apartmentCta" definition={apartmentCtaDef}>
                    <a
                      href={apartmentCta.url || '/floor-plans'}
                      target={apartmentCta._target || '_self'}
                      className={`amenities-cta ${CTA_SOLID} ${CTA_SURFACE_HOVER}`}
                      style={ctaOverLight}
                    >
                      {apartmentCta.label || 'See Floor Plans'}
                    </a>
                  </EditableSlotGroup>
                }
                headingText={apartmentHeadingText}
                items={apartmentItems}
                photos={apartmentPhotos}
                imagePosition="left"
              />
            </div>
          )}

          {/* Full-width feature band */}
          <div ref={bandRef} className="reveal-fade-up amenities-band">
            <EditableSlotGroup
              groupPath="amenitieslist.bandImage"
              definition={bandImageDef}
              className="amenities-band-media"
            >
              {bandImage.image ? (
                <VibesitesImage
                  url={bandImage.image}
                  alt={bandImage.alt || ''}
                  sizes="100vw"
                  className="w-full h-full object-cover"
                  priority
                />
              ) : (
                <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
              )}
            </EditableSlotGroup>
            <div className="amenities-band-overlay" aria-hidden="true" />
            <div className="amenities-band-inner">
              <RichTextContent
                path="amenitieslist.bandHeading"
                label="Feature Band Heading"
                defaultValue="<p>Urban Living At Its Finest</p>"
                as="h2"
                className="amenities-band-heading"
              />
              <EditableSlotGroup groupPath="amenitieslist.bandCta" definition={bandCtaDef}>
                <a
                  href={bandCta.url || '/neighborhood'}
                  target={bandCta._target || '_self'}
                  className={`amenities-cta ${CTA_OUTLINE}`}
                  style={ctaOverPhoto}
                >
                  {bandCta.label || 'Explore The Neighborhood'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {communityItems.length > 0 && (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <AmenityRow
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    label="Community Heading"
                    defaultValue="<p>Community comforts and features to enjoy.</p>"
                    as="h2"
                    className="amenities-col-heading"
                  />
                }
                ctaNode={
                  <EditableSlotGroup groupPath="amenitieslist.communityCta" definition={communityCtaDef}>
                    <a
                      href={communityCta.url || '/gallery'}
                      target={communityCta._target || '_self'}
                      className={`amenities-cta ${CTA_SOLID} ${CTA_SURFACE_HOVER}`}
                      style={ctaOverLight}
                    >
                      {communityCta.label || 'View Gallery'}
                    </a>
                  </EditableSlotGroup>
                }
                headingText={communityHeadingText}
                items={communityItems}
                photos={communityPhotos}
                imagePosition="right"
              />
            </div>
          )}
        </>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        </div>
      )}

      <style>{`
        /* Feature row: image slider beside a single-column list + CTA. */
        .amenities-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
          padding: 2.5rem 0;
        }
        .amenities-row--no-image {
          grid-template-columns: 1fr;
          max-width: 46rem;
          margin: 0 auto;
          text-align: center;
        }

        .amenities-row-content {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;
        }
        .amenities-row--no-image .amenities-row-content {
          align-items: center;
        }

        /* Image panel — holds the arched slider, aligned toward the outer edge. */
        .amenities-row-image {
          display: flex;
          width: 100%;
        }
        .amenities-row--img-left .amenities-row-image { justify-content: flex-start; }
        .amenities-row--img-right .amenities-row-image { justify-content: flex-end; }

        @media (max-width: 768px) {
          .amenities-row {
            grid-template-columns: 1fr;
            gap: 1.75rem;
            padding: 1.75rem 0;
            text-align: center;
          }
          .amenities-row-content {
            align-items: center;
          }
          /* Image always on top when stacked, regardless of desktop stagger. */
          .amenities-row--img-right > .amenities-row-content { order: 2; }
          .amenities-row--img-right > .amenities-row-image { order: 1; }
          /* Center the arched slider when stacked. (Nav centering lives with
             the slider base rules below so source order lets it win.) */
          .amenities-row .amenities-row-image { justify-content: center; }
        }

        /* Photo slider — arched image frame with the nav controls below it. */
        .amenities-slider {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: 100%;
          max-width: 27rem;
        }
        /* Arched frame: primary-color border with an inner padding gap, matching
           the arched images on the home page (PinnedCallouts). */
        .amenities-slider-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 5;
          border: 2px solid var(--vibe-color-primary);
          border-radius: 50% 50% 10px 10px / 34% 34% 10px 10px;
          padding: 14px;
        }
        .amenities-slider-clip {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border-radius: 50% 50% 6px 6px / 30% 30% 6px 6px;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        /* Nav controls: solid dark circular buttons below the image, left-aligned. */
        .amenities-slider-nav {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
        }
        .amenities-slider-nav-btn {
          width: 44px;
          height: 44px;
          padding: 0;
          border-radius: 50%;
          border: none;
          background: var(--vibe-color-text);
          color: var(--vibe-color-background);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.25s ease, transform 0.25s ease;
        }
        .amenities-slider-nav-btn:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-2px);
        }
        .amenities-slider-nav-btn:active {
          transform: translateY(0);
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        /* Section headings */
        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.9rem, 3.5vw, 2.75rem);
          font-weight: 700;
          color: var(--vibe-color-primary);
          line-height: 1.1;
          margin: 0 0 1.5rem;
          letter-spacing: 0.01em;
        }

        /* Single-column amenity list */
        .amenities-feature-list {
          list-style: none;
          padding: 0;
          margin: 0 0 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 100%;
        }
        .amenities-feature-item {
          display: flex;
          align-items: baseline;
          gap: 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          line-height: 1.5;
        }
        .amenities-row--no-image .amenities-feature-item {
          justify-content: center;
        }
        .amenities-feature-bullet {
          flex-shrink: 0;
          width: 11px;
          height: 11px;
          margin-top: 0.4em;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        .amenities-cta {
          align-self: flex-start;
        }
        .amenities-row--no-image .amenities-cta {
          align-self: center;
        }

        @media (max-width: 768px) {
          .amenities-col-heading {
            margin-bottom: 1rem;
          }
          .amenities-feature-item {
            justify-content: center;
            font-size: 1rem;
          }
          .amenities-cta,
          .amenities-row-content .amenities-cta {
            align-self: center;
          }
          /* Center the arched slider and its nav arrows when stacked. Declared
             after the base slider rules so it wins on equal specificity. */
          .amenities-slider {
            align-items: center;
          }
          .amenities-slider-nav {
            justify-content: center;
          }
        }

        /* Full-width feature band */
        .amenities-band {
          position: relative;
          overflow: hidden;
          min-height: 240px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 3.5rem 0;
          padding: 2.5rem 1.5rem;
        }
        .amenities-band-media {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .amenities-band-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.55));
        }
        .amenities-band-inner {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 2.5rem;
          text-align: left;
          width: 100%;
          max-width: 72rem;
        }
        .amenities-band-inner .amenities-cta {
          flex-shrink: 0;
        }
        .amenities-band-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 4vw, 3.25rem);
          font-weight: 600;
          color: #fff9f3;
          line-height: 1.05;
          margin: 0;
          letter-spacing: 0.01em;
          white-space: nowrap;
        }
        .amenities-band-heading p {
          margin: 0;
          white-space: nowrap;
        }

        /* Band CTA — outline pill matching the footer's "Apply Now" CTA
           (transparent body, white border + label over the dark photo, fills on
           hover). Only pin it to a single line here; geometry/typography come
           from the canonical .v-cta styles. */
        .amenities-band .amenities-cta {
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .amenities-band {
            min-height: 200px;
            margin: 2rem 0;
            padding: 2rem 1.25rem;
          }
          .amenities-band-inner {
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 1.5rem;
          }
          .amenities-band-heading,
          .amenities-band-heading p {
            white-space: normal;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.apartmentHeading': {
      label: 'Interior Heading',
      type: 'richtext',
      defaultValue: '<p>Everything you need is right here.</p>',
    },
    'amenitieslist.bandHeading': {
      label: 'Feature Band Heading',
      type: 'richtext',
      defaultValue: '<p>Urban Living At Its Finest</p>',
    },
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community comforts and features to enjoy.</p>',
    },
  },
  slotGroups: {
    'amenitieslist.apartmentCta': apartmentCtaDef,
    'amenitieslist.bandImage': bandImageDef,
    'amenitieslist.bandCta': bandCtaDef,
    'amenitieslist.communityCta': communityCtaDef,
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
